/** Preparação das séries para desenho. */

import type { Bucket, Series } from "../types";

const SEGUNDOS: Record<Bucket, number> = { raw: 1, "1m": 60, "5m": 300, "1h": 3600 };

export interface PontoGrafico {
  t: number;
  mean: number | null;
  /** `[min, max]` para a banda. `null` quando o balde está vazio. */
  faixa: [number, number] | null;
  count: number;
}

/**
 * Expande a série para uma grelha completa de baldes, com `null` onde não há dados.
 *
 * Sem isto os buracos desapareciam: a API só devolve baldes que existem, e um
 * gráfico ligaria o ponto antes do buraco ao ponto depois, desenhando uma reta
 * por cima de horas em que não se sabe nada. A grelha densa força a
 * descontinuidade a aparecer.
 *
 * Cobre as duas formas de não haver dados: o balde ausente (não chegou lote) e
 * o balde presente com `count: 0` (chegaram lotes, mas sem valor — o sonar sem
 * eco em maré cheia).
 */
export function densificar(serie: Series): PontoGrafico[] {
  const passo = SEGUNDOS[serie.bucket] * 1000;
  const inicio = Math.floor(Date.parse(serie.start) / passo) * passo;
  const fim = Date.parse(serie.end);

  const porInstante = new Map<number, (typeof serie.points)[number]>();
  for (const p of serie.points) porInstante.set(Date.parse(p.ts), p);

  const saida: PontoGrafico[] = [];
  for (let t = inicio; t <= fim; t += passo) {
    const p = porInstante.get(t);
    const vazio = p === undefined || p.count === 0 || p.mean === null;
    saida.push({
      t,
      mean: vazio ? null : p.mean,
      faixa: vazio || p.minimum === null || p.maximum === null ? null : [p.minimum, p.maximum],
      count: p?.count ?? 0,
    });
  }
  return saida;
}

/** Quantos baldes da grelha não têm valor. Alimenta a nota por baixo do título. */
export function contarVazios(pontos: PontoGrafico[]): number {
  return pontos.reduce((n, p) => (p.mean === null ? n + 1 : n), 0);
}

/**
 * Muda a unidade dos pontos (média e banda) por um fator.
 *
 * A API serve o sonar em mm — resolução do sensor, e a unidade certa para o
 * fio. No ecrã, mm dá números de quatro algarismos para uma grandeza que
 * varia metros com a maré; o painel converte para cm ao desenhar.
 */
export function escalar(pontos: PontoGrafico[], fator: number): PontoGrafico[] {
  return pontos.map((p) => ({
    ...p,
    mean: p.mean === null ? null : p.mean * fator,
    faixa: p.faixa === null ? null : [p.faixa[0] * fator, p.faixa[1] * fator],
  }));
}

